import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import {
  getEventLandingData,
  getEventLandingPageForEditor,
  upsertEventLandingPage,
  DEFAULT_LANDING_CONFIG,
  normalizeLandingConfig,
  normalizeLandingSessions,
} from '../../services/landingPageService';
import * as instructorsService from '../../services/instructorsService';
import LandingPage from '../LandingPage/LandingPage';
import { THEME_OPTIONS, DENSITY_OPTIONS, HERO_STYLE_OPTIONS } from '../LandingPage/theme';
import { useAuth } from '../../contexts/AuthContext';

const {
  FiExternalLink,
  FiCheck,
  FiUsers,
  FiTarget,
  FiHelpCircle,
  FiMessageSquare,
  FiGift,
  FiCalendar,
  FiImage,
  FiVideo,
  FiMonitor,
  FiSmartphone,
} = FiIcons;

export default function LandingPageTab({ eventId, event, onUpdate }) {
  const { profile, role } = useAuth();
  const [landingData, setLandingData] = useState(null);
  const [config, setConfig] = useState({ ...DEFAULT_LANDING_CONFIG });
  const [eventInstructors, setEventInstructors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState('desktop');
  const [debouncedConfig, setDebouncedConfig] = useState(config);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedConfig(config), 350);
    return () => clearTimeout(t);
  }, [config]);

  const loadAll = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    try {
      const [dataRes, landingRes, instRes] = await Promise.all([
        getEventLandingData(eventId),
        getEventLandingPageForEditor(eventId, { userId: profile?.id, role }),
        instructorsService.getEventInstructors(eventId),
      ]);
      setLandingData(dataRes.data || null);
      setConfig(normalizeLandingConfig(landingRes.data));
      setEventInstructors(instRes.data || []);
    } catch (e) {
      console.error('[LandingPageTab] load error:', e);
      toast.error('Failed to load landing page data');
    } finally {
      setLoading(false);
    }
  }, [eventId, profile?.id, role]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleSave = async () => {
    if (!eventId) return;
    setSaving(true);
    try {
      const { error } = await upsertEventLandingPage(eventId, config, { userId: profile?.id, role });
      if (error) throw error;
      toast.success('Landing page settings saved');
      loadAll();
      onUpdate?.();
    } catch (err) {
      toast.error(err?.message ?? 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = () => {
    window.open(`/landing/${eventId}`, '_blank');
  };

  const checklist = [
    { label: 'Event title', completed: !!event?.title, icon: FiCheck },
    { label: 'Event description', completed: !!event?.description, icon: FiCheck },
    { label: 'Featured image', completed: !!(event?.featured_image_url || event?.image_url), icon: FiImage },
    { label: 'Event sessions', completed: (event?.sessions?.length || landingData?.sessions?.length || 0) > 0, icon: FiCalendar },
    { label: 'Instructors added', completed: (eventInstructors?.length || landingData?.instructors?.length || 0) > 0, icon: FiUsers },
    { label: 'Learning objectives', completed: (landingData?.learning_objectives?.length || 0) > 0, icon: FiTarget },
    { label: 'FAQs added', completed: (landingData?.faqs?.length || 0) > 0, icon: FiHelpCircle },
    { label: 'Testimonials added', completed: (landingData?.testimonials?.length || 0) > 0, icon: FiMessageSquare },
    { label: 'Bonuses added', completed: (landingData?.bonuses?.length || 0) > 0, icon: FiGift },
    { label: 'Promo video', completed: !!event?.video_url, icon: FiVideo },
  ];
  const completedCount = checklist.filter((i) => i.completed).length;
  const completionPercentage = Math.round((completedCount / checklist.length) * 100);

  const previewSessions = normalizeLandingSessions(
    landingData?.sessions ?? event?.sessions ?? []
  );
  const previewInstructors = eventInstructors?.length
    ? eventInstructors
    : (landingData?.instructors ?? []);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Theme + density + hero style */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="font-semibold text-gray-900 mb-2">Landing Page Appearance</h3>
        <p className="text-sm text-gray-500 mb-4">
          Choose a brand theme, layout density, and hero style. Sections appear automatically when event content exists.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Theme</label>
            <select
              value={config.theme}
              onChange={(e) => setConfig((c) => ({ ...c, theme: e.target.value }))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              {THEME_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Density</label>
            <select
              value={config.density}
              onChange={(e) => setConfig((c) => ({ ...c, density: e.target.value }))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              {DENSITY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Compact hides Sessions and Bonuses even when data exists.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Hero Style</label>
            <select
              value={config.hero_style}
              onChange={(e) => setConfig((c) => ({ ...c, hero_style: e.target.value }))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              {HERO_STYLE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Image + CTA sends visitors to the standalone registration page.
            </p>
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Landing Page</h2>
          <p className="text-sm text-gray-500 mt-1">
            Content comes from event details, sessions, instructors, and landing content tabs.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handlePreview}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
          >
            <SafeIcon icon={FiExternalLink} className="w-4 h-4 mr-2" />
            Open Landing Page
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>

      {/* Checklist */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900">Content Checklist</h3>
          <span className="text-sm font-medium text-blue-600">{completionPercentage}% complete</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
          <div
            className="bg-blue-600 h-2 rounded-full transition-all"
            style={{ width: `${completionPercentage}%` }}
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {checklist.map((item) => (
            <div
              key={item.label}
              className={`flex items-center gap-2 text-sm ${item.completed ? 'text-green-700' : 'text-gray-500'}`}
            >
              <SafeIcon
                icon={item.icon}
                className={`w-4 h-4 ${item.completed ? 'text-green-500' : 'text-gray-400'}`}
              />
              {item.label}
            </div>
          ))}
        </div>
      </div>

      {/* Live preview */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="border-b px-4 py-3 bg-gray-50 flex items-center justify-between">
          <h3 className="font-semibold text-gray-900">Live Preview</h3>
          <div className="flex gap-1">
            <button
              type="button"
              onClick={() => setPreviewMode('desktop')}
              className={`p-2 rounded ${previewMode === 'desktop' ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:bg-gray-100'}`}
              title="Desktop"
            >
              <SafeIcon icon={FiMonitor} className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={() => setPreviewMode('mobile')}
              className={`p-2 rounded ${previewMode === 'mobile' ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:bg-gray-100'}`}
              title="Mobile"
            >
              <SafeIcon icon={FiSmartphone} className="w-5 h-5" />
            </button>
          </div>
        </div>
        <div
          className={`overflow-auto bg-gray-100 p-2 ${previewMode === 'mobile' ? 'max-w-[375px] mx-auto min-h-[500px]' : 'min-h-[500px]'}`}
        >
          <LandingPage
            event={event}
            sessions={previewSessions}
            learningObjectives={landingData?.learning_objectives ?? []}
            faqs={landingData?.faqs ?? []}
            testimonials={landingData?.testimonials ?? []}
            bonuses={landingData?.bonuses ?? []}
            instructors={previewInstructors}
            theme={debouncedConfig.theme}
            density={debouncedConfig.density}
            heroStyle={debouncedConfig.hero_style}
          />
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-semibold text-blue-900 mb-2">How sections work</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Sections render in a fixed order when content exists — no manual toggles needed</li>
          <li>• Hero style: embedded form (default) or featured image with register CTA</li>
          <li>• Compact density skips Sessions and Bonuses for single-session events</li>
          <li>• Edit event title, description, sessions, and content tabs to update the page</li>
        </ul>
      </div>
    </div>
  );
}
